import React, {
  useMemo,
  useState,
  useCallback,
  useContext,
  useRef,
} from 'react';
import { useHistory, generatePath } from 'react-router-dom';
import { useFormatNumber } from 'Utils/formatNumber';
import { AuthContext } from 'States/auth/authState';
import { LangContext } from 'States/lang/langState';
import { getOrderTableColumns } from 'Helpers/order';
import useHandleError from 'Utils/handleError';
import Table from 'Components/shared/Table/Table';
import { getOdataOrderByString } from 'Utils/utils';
import api from 'Api';
import Fade from 'Components/shared/Fade';
import Header from 'Components/shared/Header/Header';
import i18nextTranslate from 'Lang/i18nextTranslate';
import { i18nextKeys } from 'Lang/i18nextKeys';
import { ROUTES } from 'Router/Routes';

const OrderHistory = () => {
  const formatNumber = useFormatNumber();
  const PAGE_SIZE = 25;
  const {
    user: {
      profile: { sub },
    },
  } = useContext(AuthContext);
  const { lang } = useContext(LangContext);

  const handleError = useHandleError();
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(true);
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [pageCount, setPageCount] = useState(0);
  const fetchIdRef = useRef(0);

  const fetchData = useCallback((pageIndex, pageSize, [orderBy], _filters) => {
    const fetchId = ++fetchIdRef.current;
    setIsLoading(true);

    (async () => {
      try {
        // Abort if this is not the latest fetch
        if (fetchId !== fetchIdRef.current) {
          return;
        }

        const res = await api.PurchaseOrder.list({
          orderBy: orderBy && getOdataOrderByString(orderBy),
          top: pageSize,
          skip: pageSize * pageIndex,
          filter: `UserId eq ${sub}`,
        });
        const { value } = res;
        const totalCount = res['@odata.count'];

        setPageCount(Math.ceil(totalCount / PAGE_SIZE));
        setPurchaseOrders(value);
        setIsLoading(false);
      } catch (error) {
        const message = i18nextTranslate(
          i18nextKeys.errorOrderHistoryNoRequiredData
        );
        handleError({ error, message });
      }
    })();
  });

  const columns = useMemo(
    () => getOrderTableColumns({ formatNumber, singleUser: true }),
    [lang]
  );

  const toAccountView = () => {
    history.push(ROUTES.account);
  };

  const toPurchaseOrderDetails = ({ original: { Id } }) => {
    history.push(generatePath(ROUTES.orderDetails, { id: Id }));
  };

  return (
    <div data-qa="order-history" className="select-none">
      <Fade show={true}>
        <Header
          text={i18nextTranslate(i18nextKeys.orderHistoryHeader)}
          arrowBack
          onArrowBackClick={toAccountView}
        />
        <div className="w-full flex flex-col items-center">
          <Table
            dataQa="order-history"
            defaultSortBy="ReceivedOn"
            defaultPageSize={PAGE_SIZE}
            columns={columns}
            pageCount={pageCount}
            fetchData={fetchData}
            isLoading={isLoading}
            data={purchaseOrders}
            getRowProps={(row) => ({
              onClick: () => toPurchaseOrderDetails(row),
            })}
          />
        </div>
      </Fade>
    </div>
  );
};
export default OrderHistory;
